//Add Edit event schema with yup and reacthook form

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createEvent, setEvent, updateEvent } from '../../features/event/eventSlice';
import gameFormatService from '../../features/gameFormat/gameFormatService';
import venueService from '../../features/venue/venueService';

import { doc, onSnapshot } from 'firebase/firestore';
import { store } from '../../config/firebase';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Link as MuiLink,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import PrimaryButton from '../ui/buttons/primaryButton';

import DeleteIcon from '@mui/icons-material/DeleteOutline';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import FIELD_INFO from '../../constants/fieldInfo';
import { SCORING_METHOD_LABELS } from '../../constants/scoringMethods';
import GameFormatDescription from '../gameFormats/description';
import GameFormatWizardDialog from '../gameFormats/gameFormatWizardDialog';
import OverrideGameFormat from '../gameFormats/override';
import GreenSetup from '../greenSetup';
import Checkbox from '../ui/forms/fields/checkbox';
import DatePicker from '../ui/forms/fields/datePicker';
import FieldWithInfoBase from '../ui/fieldWithInfo';
import InfoButton from '../ui/infoButton';
import Input from '../ui/forms/fields/input';
import MultiSelect from '../ui/forms/fields/multiSelect';
import Select from '../ui/forms/fields/select';
import {
  MAX_ROUND,
  formatScheduleEntry,
  isBlankScheduleRow,
  normalizeScheduleRows,
  scheduleEntryLabel,
  scheduleRowsFromStored,
  seedScheduleRows
} from '../../helpers/roundSchedule';
import PlayersImportSection from './playersImportSection';
import usePlayersImport from './usePlayersImport';
import ViewPlayersDialog from '../players/viewPlayersDialog';
import ViewTeamsDialog from '../teams/viewTeamsDialog';
import ExcelPlayersImport from '../players/excelPlayersImport';
import RoundScheduleEditor from './roundScheduleEditor';

const FieldWithInfo = ({ infoKey, children }) => (
  <FieldWithInfoBase info={FIELD_INFO.event[infoKey]}>{children}</FieldWithInfoBase>
);

const GROUP_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const FIELD_GAP = 1.5;

const SECTION_TOP_MARGIN = 1.5;
const DEFAULT_VENUE_GREENS = 3;
const DEFAULT_VENUE_RINKS_PER_GREEN = 6;

// Canonical "blank" form state. Reused by every `reset()` call in this
// component (initial mount, cancel/close, and after a successful save) so no
// field is ever accidentally left as `undefined` - which Firestore rejects.
const EMPTY_FORM_VALUES = {
  name: '',
  description: '',
  startDate: null,
  endDate: null,
  venue: '',
  locations: [],
  gameFormat: '',
  hasSections: false,
  hasBracketOnly: false,
  seedingTemplate: '',
  publishLogs: true,
  publishScorecard: true,
  publishEvent: true,
  roundPublishMethod: 'auto-rounds',
  scoringMethod: 'endbyend',
  importPlayers: false,
  importFromEventId: '',
  importLogType: 'current',
  importSectionId: '',
  importPositionFrom: 1,
  importPositionTo: 1,
  importSkipSectionPositions: 0,
  importOrderByPbaRanking: false,
  importPbaRankingDate: null,
  importAutoGroupPlayers: true,
  importShowPastEvents: false,
  useGroups: false,
  useMultipleVenues: false,
  venueNotApplicable: false,
  venueGreensCount: DEFAULT_VENUE_GREENS,
  venueRinksPerGreen: DEFAULT_VENUE_RINKS_PER_GREEN
};

const toPositiveInt = (value, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }

  return Math.floor(parsed);
};

const getAlphabetLabel = (index) => {
  let current = index;
  let label = '';

  while (current >= 0) {
    label = String.fromCharCode((current % 26) + 65) + label;
    current = Math.floor(current / 26) - 1;
  }

  return label;
};

const buildGreensFromCounts = (greensCount, rinksPerGreen) => {
  const totalGreens = toPositiveInt(greensCount, DEFAULT_VENUE_GREENS);
  const totalRinksPerGreen = toPositiveInt(rinksPerGreen, DEFAULT_VENUE_RINKS_PER_GREEN);

  return Array.from({ length: totalGreens }, (_, index) => ({
    name: getAlphabetLabel(index),
    rink_count: totalRinksPerGreen
  }));
};

const getVenueCounts = (venue) => {
  if (!venue || !Array.isArray(venue.greens) || venue.greens.length === 0) {
    return {
      greensCount: DEFAULT_VENUE_GREENS,
      rinksPerGreen: DEFAULT_VENUE_RINKS_PER_GREEN
    };
  }

  const greensCount = venue.greens.length;
  const firstGreen = venue.greens[0] || {};
  const rinksPerGreen =
    firstGreen.rink_count ||
    (Array.isArray(firstGreen.rinks) ? firstGreen.rinks.length : DEFAULT_VENUE_RINKS_PER_GREEN);

  return {
    greensCount: toPositiveInt(greensCount, DEFAULT_VENUE_GREENS),
    rinksPerGreen: toPositiveInt(rinksPerGreen, DEFAULT_VENUE_RINKS_PER_GREEN)
  };
};

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  description: yup.string(),
  startDate: yup.string().required('Start Date is required'),
  endDate: yup.string().required('End Date is required'),
  gameFormat: yup.string(),
  venue: yup.string(),
  hasSections: yup.bool().default(false),
  hasBracketOnly: yup.bool().default(false),
  seedingTemplate: yup.string().default(''),
  publishLogs: yup.bool().default(true),
  publishScorecard: yup.bool().default(true),
  publishEvent: yup.bool().default(true),
  roundPublishMethod: yup.string().oneOf(['auto-rounds', 'publish-rounds']).default('auto-rounds'),
  scoringMethod: yup.string().oneOf(['endbyend', 'inputfinalscore', 'inputtotals']).default('endbyend')
});

const AddEditEventDialog = ({ open, onClose, event, importedEvent }) => {
  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...EMPTY_FORM_VALUES,
      locations: event?.locations ? event.locations.map((loc) => loc.id) : []
    }
  });

  const {
    register,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = formMethods;

  const dispatch = useDispatch();
  const saving = useSelector((state) => state.event.saving);
  const events = useSelector((state) => state.event.events);
  const liveEvent = useSelector((state) => state.event.event);
  const [venues, setVenues] = React.useState([]);
  const [gameFormats, setGameFormats] = React.useState([]);
  const [lookupLoaded, setLookupLoaded] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [draftEventId, setDraftEventId] = useState(null);
  const [draftError, setDraftError] = useState('');
  const [gameFormatWizardMode, setGameFormatWizardMode] = useState(null);
  const greenSetupRef = useRef(null);
  const [savingGameFormat, setSavingGameFormat] = useState(false);
  // const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  const [selectedGameFormat, setSelectedGameFormat] = React.useState(
    event ? event.gameFormat : null
  );
  const [groupEntries, setGroupEntries] = useState([]);
  const [groupNameInput, setGroupNameInput] = useState('');
  const [groupColorInput, setGroupColorInput] = useState('#1976d2');
  const [groupError, setGroupError] = useState('');
  const groupColorInputRefs = useRef({});
  const [roundScheduleRows, setRoundScheduleRows] = useState([]);
  const [roundScheduleError, setRoundScheduleError] = useState('');
  // Tracks whether the user has touched the schedule, so changing the game
  // format (or deleting every row) cannot silently re-seed over their edits.
  const roundScheduleTouchedRef = useRef(false);
  const sortedVenues = useMemo(
    () =>
      [...venues].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
          sensitivity: 'base'
        })
      ),
    [venues]
  );
  const sortedGameFormats = useMemo(
    () =>
      [...gameFormats].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
          sensitivity: 'base'
        })
      ),
    [gameFormats]
  );

  // Watch both values
  const watchHasBracketOnly = watch('hasBracketOnly');
  const watchHasSections = watch('hasSections');
  const watchUseGroups = watch('useGroups');
  const watchUseMultipleVenues = watch('useMultipleVenues');
  const watchVenueNotApplicable = watch('venueNotApplicable');
  const watchLocations = watch('locations');
  const isEditingExistingEvent = Boolean(event);
  const workingEventId = draftEventId || event?.id || null;

  const showGreenSetupStep =
    !watchVenueNotApplicable &&
    (watchUseMultipleVenues ? (watchLocations || []).length > 0 : Boolean(watch('venue')));

  const wizardSteps = useMemo(() => {
    const steps = ['Details', 'Game Format', 'Round Times'];
    if (showGreenSetupStep) {
      steps.push('Green Setup');
    }
    steps.push('Players & Teams', 'Bracket Settings', 'Scoring Settings', 'Log Settings', 'Review');
    return steps;
  }, [showGreenSetupStep]);

  const currentStepLabel = wizardSteps[activeStep];

  const roundScheduleSummary = useMemo(() => {
    const result = normalizeScheduleRows(roundScheduleRows);

    if (result.error) {
      return 'Incomplete - fix in Round Times';
    }

    if (result.roundSchedule.length === 0) {
      return 'Not set';
    }

    return result.roundSchedule
      .map((entry) => `${scheduleEntryLabel(entry)} ${formatScheduleEntry(entry)}`)
      .join('; ');
  }, [roundScheduleRows]);

  const workingEvent = useMemo(() => {
    // The live document is the freshest copy once a draft exists; the list and
    // the row's own event are fallbacks for the first render.
    if (liveEvent?.id && liveEvent.id === workingEventId) {
      return liveEvent;
    }
    return events.find((existingEvent) => existingEvent.id === workingEventId) || event || null;
  }, [events, workingEventId, event, liveEvent]);

  useEffect(() => {
    setActiveStep((prev) => (prev > wizardSteps.length - 1 ? wizardSteps.length - 1 : prev));
  }, [wizardSteps]);

  // Keep `state.event.event` live while the wizard is open on the
  // "Players & Teams" step, since ViewPlayersDialog/ViewTeamsDialog (and the
  // dialogs they render) read player/team data from that global slot rather
  // than from the `event` prop passed into this component.
  useEffect(() => {
    if (!open || !workingEventId) {
      return;
    }

    const docRef = doc(store, 'events', workingEventId);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        dispatch(setEvent({ id: docSnap.id, ...docSnap.data() }));
      }
    });

    return () => {
      unsubscribe();
    };
  }, [open, workingEventId, dispatch]);

  // Auto-enable "Use Sides" (and pull in any newly created sides) when
  // players are imported with side/group data allocated - e.g. via the
  // Excel template's "Side" column. This only ever turns sides on and adds
  // missing entries; it never overrides a side the user has explicitly
  // switched off or edited locally, so "Use Sides" can still be switched
  // off afterwards.
  useEffect(() => {
    if (!workingEventId || !liveEvent || liveEvent.id !== workingEventId) {
      return;
    }

    if (liveEvent.useGroups && !watchUseGroups) {
      setValue('useGroups', true);
    }

    const liveGroups = Array.isArray(liveEvent.teamGroups) ? liveEvent.teamGroups : [];
    if (liveGroups.length === 0) {
      return;
    }

    setGroupEntries((prev) => {
      const existingIds = new Set(prev.map((group) => group.id));
      const newGroups = liveGroups
        .filter((group) => group?.id && !existingIds.has(group.id))
        .map((group) => ({
          id: group.id,
          name: String(group.name || '').trim(),
          color: GROUP_COLOR_PATTERN.test(group.color || '') ? group.color : ''
        }));

      if (newGroups.length === 0) {
        return prev;
      }

      return [...prev, ...newGroups];
    });
  }, [liveEvent, workingEventId, watchUseGroups, setValue]);

  useEffect(() => {
    if (!open || lookupLoaded) return;

    const loadLookups = async () => {
      await Promise.all([getVenues(), getGameFormats()]);
      setLookupLoaded(true);
    };

    loadLookups();
  }, [open, lookupLoaded]);

  useEffect(() => {
    if (event) {
      setSelectedGameFormat(event.gameFormat);

      const venueCounts = getVenueCounts(event.venue);

      reset({
        name: event.name || '',
        description: event.description || '',
        startDate: event.startDate ? moment(event.startDate) : null,
        endDate: event.endDate ? moment(event.endDate) : null,
        venue:
          event.venue?.id && event.venue?.id !== 'multiple' && event.venue?.id !== 'na'
            ? event.venue.id
            : '',
        locations: event?.locations ? event.locations.map((loc) => loc.id) : [],
        gameFormat: event.gameFormat?.id || '',
        hasSections: Boolean(event.hasSections),
        hasBracketOnly: Boolean(event.hasBracketOnly),
        seedingTemplate: event.seedingTemplate || '',
        publishLogs: event.publishLogs !== undefined ? Boolean(event.publishLogs) : true,
        publishScorecard:
          event.publishScorecard !== undefined ? Boolean(event.publishScorecard) : true,
        // Absent means publish. Must stay the `!== undefined` form: a plain
        // Boolean() would coerce a legacy event's missing field to false and
        // silently unpublish it on the next save.
        publishEvent: event.publishEvent !== undefined ? Boolean(event.publishEvent) : true,
        roundPublishMethod: event.roundPublishMethod || 'auto-rounds',
        scoringMethod: event.scoringMethod || 'endbyend',
        importPlayers: false,
        importFromEventId: '',
        importLogType: 'current',
        importSectionId: '',
        importPositionFrom: 1,
        importPositionTo: 1,
        importSkipSectionPositions: 0,
        importOrderByPbaRanking: false,
        importPbaRankingDate: null,
        importAutoGroupPlayers: true,
        importShowPastEvents: false,
        useGroups: Array.isArray(event.teamGroups) && event.teamGroups.length > 0,
        useMultipleVenues: event.venue?.id === 'multiple',
        venueNotApplicable: !event.venue || event.venue?.id === 'na',
        venueGreensCount: venueCounts.greensCount,
        venueRinksPerGreen: venueCounts.rinksPerGreen
      });

      setGroupEntries(
        Array.isArray(event.teamGroups)
          ? event.teamGroups.map((group) => ({
              id: group.id || uuidv4(),
              name: String(group.name || '').trim(),
              color: GROUP_COLOR_PATTERN.test(group.color || '') ? group.color : ''
            }))
          : []
      );
      setGroupNameInput('');
      setGroupColorInput('#1976d2');
      setGroupError('');
      // Seed inside the load rather than in the effect below: the effect sees
      // the previous (empty) rows in the same commit and would overwrite the
      // stored schedule with placeholders.
      const storedScheduleRows = scheduleRowsFromStored(event.roundSchedule);
      if (storedScheduleRows.length > 0) {
        setRoundScheduleRows(storedScheduleRows);
      } else {
        const seedCount = Math.min(
          toPositiveInt(event.gameFormat?.rounds, 0),
          MAX_ROUND
        );
        setRoundScheduleRows(seedCount > 0 ? seedScheduleRows(seedCount) : []);
      }
      setRoundScheduleError('');
      roundScheduleTouchedRef.current = false;
      setDraftEventId(event.id || null);
      setActiveStep(0);
      setDraftError('');
    }

    return () => {
      setSelectedGameFormat(null);
    };
  }, [event, reset]);

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setDraftEventId(event?.id || null);
      setDraftError('');
      setRoundScheduleRows([]);
      setRoundScheduleError('');
      roundScheduleTouchedRef.current = false;
    }
  }, [open, event?.id]);

  useEffect(() => {
    if (importedEvent) {
      setValue('name', importedEvent.name);
      setValue('description', importedEvent.description);
      setValue('startDate', moment(importedEvent.startDate));
      setValue('endDate', moment(importedEvent.endDate));
    }
  }, [importedEvent, setValue]);

  const watchGameFormat = watch('gameFormat');
  useEffect(() => {
    if (!watchGameFormat) {
      return;
    }

    // When editing an event, event.gameFormat carries the event's overridden
    // values and takes precedence over the shared format from the list.
    if (event?.gameFormat?.id === watchGameFormat) {
      setSelectedGameFormat(event.gameFormat);
      return;
    }

    const gameFormatObject = gameFormats.find((gf) => gf.id === watchGameFormat);
    setSelectedGameFormat(gameFormatObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchGameFormat, gameFormats, event?.gameFormat?.id]);

  // Seed one row per round from the selected game format until the user starts
  // editing the schedule. An existing event's rows were seeded when its stored
  // schedule was loaded above, so it is left alone here.
  useEffect(() => {
    if (!open || event?.id || !selectedGameFormat || roundScheduleTouchedRef.current) {
      return;
    }

    if (roundScheduleRows.some((row) => !isBlankScheduleRow(row))) {
      return;
    }

    const count = Math.min(toPositiveInt(selectedGameFormat.rounds, 0), MAX_ROUND);

    if (count === 0 || roundScheduleRows.length === count) {
      return;
    }

    setRoundScheduleRows(seedScheduleRows(count));
  }, [open, selectedGameFormat, roundScheduleRows, event?.id]);

  useEffect(() => {
    if (watchHasBracketOnly) {
      setValue('hasSections', false);
    }
  }, [watchHasBracketOnly, setValue]);

  useEffect(() => {
    if (watchVenueNotApplicable) {
      setValue('useMultipleVenues', false);
      setValue('venue', '');
      setValue('locations', []);
    }
  }, [watchVenueNotApplicable, setValue]);

  useEffect(() => {
    if (watchUseMultipleVenues) {
      setValue('venueNotApplicable', false);
      setValue('venue', '');
    }
  }, [watchUseMultipleVenues, setValue]);

  const watchVenue = watch('venue');
  useEffect(() => {
    if (!watchVenue || watchUseMultipleVenues || watchVenueNotApplicable) {
      setValue('venueGreensCount', DEFAULT_VENUE_GREENS);
      setValue('venueRinksPerGreen', DEFAULT_VENUE_RINKS_PER_GREEN);
      return;
    }

    const selectedVenue = venues.find((venue) => venue.id === watchVenue);
    const counts = getVenueCounts(selectedVenue);

    setValue('venueGreensCount', counts.greensCount);
    setValue('venueRinksPerGreen', counts.rinksPerGreen);
  }, [watchVenue, watchUseMultipleVenues, watchVenueNotApplicable, venues, setValue]);

  useEffect(() => {
    if (watchHasSections) {
      setValue('hasBracketOnly', false);
    }
  }, [watchHasSections, setValue]);

  const getVenues = async () => {
    const venues = await venueService.getAll();
    setVenues(venues);
  };

  const getGameFormats = async () => {
    const gameFormats = await gameFormatService.getAll();
    setGameFormats(gameFormats);
  };

  const openCreateGameFormatWizard = () => {
    setGameFormatWizardMode('create');
  };

  const openEditGameFormatWizard = () => {
    if (!selectedGameFormat) {
      setDraftError('Select a game format first.');
      return;
    }

    setGameFormatWizardMode('edit');
  };

  const closeGameFormatWizard = () => {
    setGameFormatWizardMode(null);
    setDraftError('');
  };

  const handleSaveManagedGameFormat = async (payload) => {
    try {
      setSavingGameFormat(true);
      if (gameFormatWizardMode === 'edit' && selectedGameFormat?.id) {
        // Edit the shared format document based on its current list values,
        // not the event's overridden copy in selectedGameFormat.
        const sharedFormat = gameFormats.find((gf) => gf.id === selectedGameFormat.id);
        const updatedFormat = await gameFormatService.update({
          ...(sharedFormat || selectedGameFormat),
          ...payload
        });
        await getGameFormats();
        setValue('gameFormat', updatedFormat.id);
        setSelectedGameFormat(updatedFormat);
      } else {
        const createdFormat = await gameFormatService.create(payload);
        await getGameFormats();
        setValue('gameFormat', createdFormat.id);
        setSelectedGameFormat(createdFormat);
      }

      setGameFormatWizardMode(null);
      setDraftError('');
    } catch (error) {
      setDraftError(error?.response?.data || 'Unable to save game format.');
    } finally {
      setSavingGameFormat(false);
    }
  };

  const handleDeleteSelectedGameFormat = async () => {
    if (!selectedGameFormat?.id) {
      setDraftError('Select a game format first.');
      return;
    }

    if (!selectedGameFormat.workspaceId) {
      setDraftError('Global game formats cannot be deleted.');
      return;
    }

    if (!window.confirm(`Delete game format "${selectedGameFormat.name}"?`)) {
      return;
    }

    try {
      setSavingGameFormat(true);
      await gameFormatService.remove(selectedGameFormat.id);
      await getGameFormats();
      setValue('gameFormat', '');
      setSelectedGameFormat(null);
      setGameFormatWizardMode(null);
      setDraftError('');
    } catch (error) {
      setDraftError(error?.response?.data || 'Unable to delete game format.');
    } finally {
      setSavingGameFormat(false);
    }
  };

  const playersImport = usePlayersImport({
    events,
    selectedGameFormat,
    formMethods,
    event
  });

  const { watchImportPlayers, importValidationError } = playersImport;

  const handleAddGroup = () => {
    const name = String(groupNameInput || '').trim();

    if (!name) {
      setGroupError('Group name is required.');
      return;
    }

    const exists = groupEntries.some(
      (group) =>
        String(group.name || '')
          .trim()
          .toLowerCase() === name.toLowerCase()
    );

    if (exists) {
      setGroupError('Group name already exists.');
      return;
    }

    setGroupEntries((prev) => [
      ...prev,
      {
        id: uuidv4(),
        name,
        color: GROUP_COLOR_PATTERN.test(groupColorInput || '') ? groupColorInput : ''
      }
    ]);
    setGroupNameInput('');
    setGroupError('');
  };

  const handleRemoveGroup = (groupId) => {
    const groupInUse = (event?.teams || []).some((team) => team.groupId === groupId);

    if (groupInUse) {
      setGroupError(
        'This group has teams assigned and cannot be removed. You can update its color.'
      );
      return;
    }

    setGroupEntries((prev) => prev.filter((group) => group.id !== groupId));
    if (groupColorInputRefs.current[groupId]) {
      delete groupColorInputRefs.current[groupId];
    }
    setGroupError('');
  };

  const handleGroupColorChange = (groupId, color) => {
    setGroupEntries((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? { ...group, color: GROUP_COLOR_PATTERN.test(color || '') ? color : '' }
          : group
      )
    );
  };

  const handleRoundScheduleChange = (rows) => {
    roundScheduleTouchedRef.current = true;
    setRoundScheduleRows(rows);
    setRoundScheduleError('');
  };

  const buildEventPayload = async (data) => {
    // Guard against `undefined` slipping through to Firestore (which rejects
    // it outright) for fields that should always be a concrete boolean.
    data.hasSections = Boolean(data.hasSections);
    data.hasBracketOnly = Boolean(data.hasBracketOnly);
    data.publishLogs = Boolean(data.publishLogs);
    data.publishScorecard = Boolean(data.publishScorecard);
    data.publishEvent = Boolean(data.publishEvent);
    data.useGroups = Boolean(data.useGroups);
    data.useMultipleVenues = Boolean(data.useMultipleVenues);
    data.venueNotApplicable = Boolean(data.venueNotApplicable);

    const roundScheduleResult = normalizeScheduleRows(roundScheduleRows);
    if (roundScheduleResult.error) {
      setRoundScheduleError(roundScheduleResult.error);
      setDraftError(roundScheduleResult.error);
      return null;
    }

    setRoundScheduleError('');
    data.roundSchedule = roundScheduleResult.roundSchedule;

    if (!data.useGroups && (event?.teams || []).some((team) => team.groupId)) {
      setGroupError(
        'Groups cannot be disabled while teams are connected. Remove team group assignments first.'
      );
      return null;
    }

    if (data.useGroups) {
      const uniqueGroups = [];
      const seenNames = new Set();

      groupEntries.forEach((group) => {
        const name = String(group.name || '').trim();
        if (!name) return;

        const key = name.toLowerCase();
        if (seenNames.has(key)) return;
        seenNames.add(key);

        uniqueGroups.push({
          id: group.id || uuidv4(),
          name,
          color: GROUP_COLOR_PATTERN.test(group.color || '') ? group.color : ''
        });
      });

      data.teamGroups = uniqueGroups;
    } else {
      data.teamGroups = [];
    }

    if (data.venueNotApplicable) {
      data.venue = null;
      data.locations = [];
    } else if (data.useMultipleVenues) {
      data.venue = { name: 'Multiple', id: 'multiple' };

      if (data.locations) {
        data.locations = data.locations
          .map((location) => {
            const venue = venues.find((v) => v.id === location);
            return venue ? { id: venue.id, name: venue.name, greens: venue.greens } : null;
          })
          .filter(Boolean);
      }
    } else {
      const venueObject = venues.find((venue) => venue.id === data.venue);

      const venueGreensCount = toPositiveInt(data.venueGreensCount, DEFAULT_VENUE_GREENS);
      const venueRinksPerGreen = toPositiveInt(
        data.venueRinksPerGreen,
        DEFAULT_VENUE_RINKS_PER_GREEN
      );

      const nextGreens = buildGreensFromCounts(venueGreensCount, venueRinksPerGreen);

      if (venueObject) {
        const updatedVenue = {
          ...venueObject,
          greens: nextGreens
        };

        data.venue = updatedVenue;

        const currentVenueCounts = getVenueCounts(venueObject);
        const hasVenueLayoutChanged =
          currentVenueCounts.greensCount !== venueGreensCount ||
          currentVenueCounts.rinksPerGreen !== venueRinksPerGreen;

        if (hasVenueLayoutChanged) {
          await venueService.update(venueObject.id, updatedVenue);
          setVenues((prevVenues) =>
            prevVenues.map((existingVenue) =>
              existingVenue.id === updatedVenue.id ? updatedVenue : existingVenue
            )
          );
        }
      } else {
        data.venue = venueObject;
      }

      data.locations = [];
    }

    let gameFormatObject = gameFormats.find((gf) => gf.id === data.gameFormat);
    if (selectedGameFormat) {
      gameFormatObject = { ...gameFormatObject, ...selectedGameFormat };
    }

    data.gameFormat = gameFormatObject;

    if (importedEvent) {
      data.clubMasterId = importedEvent.id;
    }

    if (watchImportPlayers) {
      const importFormValues = formMethods.getValues([
        'importFromEventId',
        'importLogType',
        'importPositionFrom',
        'importPositionTo',
        'importSkipSectionPositions',
        'importOrderByPbaRanking',
        'importAutoGroupPlayers',
        'importPbaRankingDate'
      ]);

      const rawDate = importFormValues[7];
      const pbaRankingDate =
        importFormValues[5] && rawDate
          ? typeof rawDate.format === 'function'
            ? rawDate.format('YYYY-MM-DD')
            : String(rawDate)
          : undefined;

      data.playerImport = {
        fromEventId: importFormValues[0],
        logType: importFormValues[1],
        positionFrom: importFormValues[2],
        positionTo: importFormValues[3],
        skipSectionPositions: importFormValues[4],
        orderByPbaRanking: importFormValues[5],
        autoGroupPlayers: importFormValues[6],
        pbaRankingDate
      };
    }

    return data;
  };

  const validateStep = (stepIndex, values) => {
    if (stepIndex === 0) {
      if (!String(values.name || '').trim()) {
        setDraftError('Event name is required.');
        return false;
      }

      if (!values.startDate || !values.endDate) {
        setDraftError('Start date and end date are required.');
        return false;
      }
    }

    if (stepIndex === 1 && !String(values.gameFormat || '').trim()) {
      setDraftError('Game format is required.');
      return false;
    }

    if (wizardSteps[stepIndex] === 'Round Times') {
      const roundScheduleResult = normalizeScheduleRows(roundScheduleRows);
      if (roundScheduleResult.error) {
        setRoundScheduleError(roundScheduleResult.error);
        setDraftError(roundScheduleResult.error);
        return false;
      }

      setRoundScheduleError('');
    }

    setDraftError('');
    return true;
  };

  const persistDraft = async () => {
    try {
      const values = formMethods.getValues();
      const payload = await buildEventPayload({ ...values });

      if (!payload) {
        return null;
      }

      const draftPayload = {
        ...payload,
        status: 'draft'
      };

      if (workingEventId) {
        await dispatch(updateEvent({ id: workingEventId, ...draftPayload })).unwrap();
        return workingEventId;
      }

      const createdDraft = await dispatch(createEvent(draftPayload)).unwrap();
      if (createdDraft?.id) {
        setDraftEventId(createdDraft.id);
        return createdDraft.id;
      }

      return null;
    } catch (error) {
      setDraftError('Unable to save draft. Please try again.');
      return null;
    }
  };

  const persistGreenSetupIfMounted = async () => {
    if (greenSetupRef.current?.save) {
      await greenSetupRef.current.save();
    }
  };

  const handleSaveDraft = async () => {
    if (!workingEventId && activeStep < 2) {
      setDraftError('Select a game format first. Drafts can be saved from Step 2 onwards.');
      return;
    }

    const values = formMethods.getValues();
    if (!validateStep(0, values) || !validateStep(1, values)) {
      return;
    }

    await persistDraft();
    await persistGreenSetupIfMounted();
  };

  const handleSaveAndClose = async () => {
    if (!workingEventId && activeStep < 2) {
      setDraftError('Select a game format first. Drafts can be saved from Step 2 onwards.');
      return;
    }

    const values = formMethods.getValues();
    if (!validateStep(0, values) || !validateStep(1, values)) {
      return;
    }

    const savedId = await persistDraft();
    if (!savedId) {
      return;
    }

    await persistGreenSetupIfMounted();
    handleClose();
  };

  const handleNextStep = async () => {
    const values = formMethods.getValues();
    if (!validateStep(activeStep, values)) {
      return;
    }

    // For new events, persist draft once Details + Game Format are completed.
    if (currentStepLabel === 'Game Format' && !workingEventId) {
      const createdId = await persistDraft();
      if (!createdId) {
        return;
      }
    }

    await persistGreenSetupIfMounted();

    if (activeStep < wizardSteps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePreviousStep = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleCompleteWizard = async () => {
    const values = formMethods.getValues();
    if (!validateStep(0, values) || !validateStep(1, values)) {
      return;
    }

    try {
      const payload = await buildEventPayload({ ...values });
      if (!payload) {
        return;
      }

      const finalPayload = {
        ...payload,
        status: 'published',
        completedAt: new Date().toISOString()
      };

      if (workingEventId) {
        await dispatch(updateEvent({ id: workingEventId, ...finalPayload })).unwrap();
      } else {
        await dispatch(createEvent(finalPayload)).unwrap();
      }

      reset({
        ...EMPTY_FORM_VALUES,
        startDate: moment(),
        endDate: moment()
      });
      setGroupEntries([]);
      setGroupNameInput('');
      setGroupColorInput('#1976d2');
      setGroupError('');
      setRoundScheduleRows([]);
      setRoundScheduleError('');
      roundScheduleTouchedRef.current = false;
      handleClose();
    } catch (error) {
      setDraftError('Unable to complete event setup. Please try again.');
    }
  };

  const handleClose = () => {
    reset({
      ...EMPTY_FORM_VALUES,
      startDate: moment(),
      endDate: moment()
    });
    setGroupEntries([]);
    setGroupNameInput('');
    setGroupColorInput('#1976d2');
    setGroupError('');
    setRoundScheduleRows([]);
    setRoundScheduleError('');
    roundScheduleTouchedRef.current = false;
    setSelectedGameFormat(null);
    dispatch(setEvent(null));
    onClose();
  };

  const handleAddEdit = () => {
    handleCompleteWizard();
  };

  const handleGameFormatChange = (gameFormat) => {
    setSelectedGameFormat(gameFormat);
  };

  const disableSave =
    saving || isSubmitting || (watchImportPlayers && Boolean(importValidationError));
  const canSaveDraft =
    !disableSave &&
    (Boolean(workingEventId) ||
      (activeStep >= 2 && Boolean(String(watch('gameFormat') || '').trim())));

  return (
    <FormProvider {...formMethods}>
      <Dialog open={open} onClose={() => handleClose()} fullWidth maxWidth="lg">
        <DialogTitle variant="h4" component="h1">
          {event ? 'Edit Event' : 'New Event'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ minWidth: { md: 260 } }}>
              <Stepper
                activeStep={activeStep}
                orientation="vertical"
                sx={{
                  '& .MuiStepIcon-root': {
                    color: (theme) =>
                      theme.palette.mode === 'dark'
                        ? theme.palette.grey[700]
                        : theme.palette.grey[400]
                  },
                  '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
                    color: 'primary.main'
                  },
                  '& .MuiStepIcon-text': {
                    fill: 'common.white'
                  }
                }}>
                {wizardSteps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            <Box sx={{ flex: 1 }}>
              {draftError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {draftError}
                </Alert>
              ) : null}

              {currentStepLabel === 'Details' && (
                <Stack gap={FIELD_GAP}>
                  <FieldWithInfo infoKey="name">
                    <Input
                      label="Name"
                      error={errors.name}
                      disabled={saving}
                      {...register('name')}
                    />
                  </FieldWithInfo>
                  <FieldWithInfo infoKey="description">
                    <Input
                      label="Description"
                      error={errors.description}
                      disabled={saving}
                      multiline
                      minRows={3}
                      {...register('description')}
                    />
                  </FieldWithInfo>
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    flexWrap="wrap"
                    gap={2}>
                    <FieldWithInfo infoKey="startDate">
                      <DatePicker
                        name={'startDate'}
                        label="Start Date"
                        control={control}
                        error={errors.startDate}
                        format={'DD/MM/YYYY'}
                        sx={{
                          minWidth: { xs: '100%', sm: 220 },
                          '& .MuiInputBase-input': {
                            color: 'text.primary'
                          },
                          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                            color: 'text.primary'
                          }
                        }}
                        disabled={saving}
                      />
                    </FieldWithInfo>
                    <FieldWithInfo infoKey="endDate">
                      <DatePicker
                        name={'endDate'}
                        label="End Date"
                        control={control}
                        error={errors.endDate}
                        format={'DD/MM/YYYY'}
                        sx={{
                          minWidth: { xs: '100%', sm: 220 },
                          '& .MuiInputBase-input': {
                            color: 'text.primary'
                          },
                          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                            color: 'text.primary'
                          }
                        }}
                        disabled={saving}
                      />
                    </FieldWithInfo>
                  </Box>

                  {!event ? (
                    <Stack gap={FIELD_GAP}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          p: 2,
                          backgroundColor: 'background.default'
                        }}>
                        <Stack gap={FIELD_GAP}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            Venue Settings
                          </Typography>

                          <FieldWithInfo infoKey="venueNotApplicable">
                            <Checkbox
                              name={'venueNotApplicable'}
                              label="Venue Not Applicable"
                              control={control}
                              disabled={saving}
                            />
                          </FieldWithInfo>

                          {!watchVenueNotApplicable && (
                            <>
                              <FieldWithInfo infoKey="useMultipleVenues">
                                <Checkbox
                                  name={'useMultipleVenues'}
                                  label="Use Multiple Clubs"
                                  control={control}
                                  disabled={saving}
                                />
                              </FieldWithInfo>

                              {!watchUseMultipleVenues && (
                                <>
                                  <FieldWithInfo infoKey="venue">
                                    <Autocomplete
                                      options={sortedVenues}
                                      value={
                                        sortedVenues.find((v) => v.id === watch('venue')) || null
                                      }
                                      onChange={(_, selectedVenueOption) => {
                                        setValue('venue', selectedVenueOption?.id || '');
                                      }}
                                      getOptionLabel={(option) => option?.name || ''}
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      disabled={saving}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Venue"
                                          size="small"
                                          placeholder="Type to search clubs"
                                          error={Boolean(errors.venue)}
                                          helperText={errors.venue?.message}
                                        />
                                      )}
                                    />
                                  </FieldWithInfo>

                                  {watch('venue') ? (
                                    <Box display="flex" flexWrap="wrap" gap={2}>
                                      <FieldWithInfo infoKey="venueGreensCount">
                                        <TextField
                                          label="Greens"
                                          type="number"
                                          size="small"
                                          value={watch('venueGreensCount') || DEFAULT_VENUE_GREENS}
                                          onChange={(event) =>
                                            setValue(
                                              'venueGreensCount',
                                              toPositiveInt(
                                                event.target.value,
                                                DEFAULT_VENUE_GREENS
                                              )
                                            )
                                          }
                                          inputProps={{ min: 1 }}
                                          sx={{ minWidth: 160 }}
                                        />
                                      </FieldWithInfo>
                                      <FieldWithInfo infoKey="venueRinksPerGreen">
                                        <TextField
                                          label="Rinks Per Green"
                                          type="number"
                                          size="small"
                                          value={
                                            watch('venueRinksPerGreen') ||
                                            DEFAULT_VENUE_RINKS_PER_GREEN
                                          }
                                          onChange={(event) =>
                                            setValue(
                                              'venueRinksPerGreen',
                                              toPositiveInt(
                                                event.target.value,
                                                DEFAULT_VENUE_RINKS_PER_GREEN
                                              )
                                            )
                                          }
                                          inputProps={{ min: 1 }}
                                          sx={{ minWidth: 180 }}
                                        />
                                      </FieldWithInfo>
                                    </Box>
                                  ) : null}
                                </>
                              )}

                              {watchUseMultipleVenues && (
                                <FieldWithInfo infoKey="locations">
                                  <MultiSelect
                                    name="locations"
                                    label="Choose clubs"
                                    control={control}
                                    defaultValue={[]}
                                    helperText="Select one or more"
                                    listValues={sortedVenues.map((venue) => ({
                                      id: venue.id,
                                      name: venue.name
                                    }))}>
                                    {sortedVenues.map((venue) => (
                                      <MenuItem key={venue.id} value={venue.id}>
                                        {venue.name}
                                      </MenuItem>
                                    ))}
                                  </MultiSelect>
                                </FieldWithInfo>
                              )}
                            </>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  ) : (
                    <Typography variant="body1">
                      Location: {event.venue?.name || 'Not Applicable'}
                    </Typography>
                  )}
                </Stack>
              )}

              {currentStepLabel === 'Game Format' && (
                <Stack gap={FIELD_GAP}>
                  {gameFormatWizardMode ? (
                    <GameFormatWizardDialog
                      embedded
                      open
                      onClose={closeGameFormatWizard}
                      onBack={closeGameFormatWizard}
                      initialValues={
                        gameFormatWizardMode === 'edit'
                          ? gameFormats.find((gf) => gf.id === selectedGameFormat?.id) ||
                            selectedGameFormat
                          : null
                      }
                      onSave={handleSaveManagedGameFormat}
                      includeName
                      title={
                        gameFormatWizardMode === 'edit' ? 'Edit Game Format' : 'Create Game Format'
                      }
                      saveLabel={gameFormatWizardMode === 'edit' ? 'Save Changes' : 'Create Format'}
                      saving={savingGameFormat}
                      onDelete={
                        gameFormatWizardMode === 'edit' && selectedGameFormat?.workspaceId
                          ? handleDeleteSelectedGameFormat
                          : null
                      }
                    />
                  ) : (
                    <>
                      <FieldWithInfo infoKey="gameFormat">
                        <Select
                          label="Game Format"
                          name="gameFormat"
                          control={control}
                          error={errors.venue}
                          disabled={saving}
                          defaultValue={event?.gameFormat?.id || ''}>
                          {sortedGameFormats.map((format) => (
                            <MenuItem key={format.id} value={format.id}>
                              <Box
                                sx={{
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 1
                                }}>
                                <span>{format.name}</span>
                                <Chip
                                  label={format.workspaceId ? 'Custom' : 'Global'}
                                  size="small"
                                  variant="filled"
                                  color={format.workspaceId ? 'secondary' : 'info'}
                                />
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FieldWithInfo>

                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Button
                          variant="outlined"
                          onClick={openCreateGameFormatWizard}
                          disabled={savingGameFormat}>
                          New Game Format
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={openEditGameFormatWizard}
                          disabled={
                            savingGameFormat ||
                            !selectedGameFormat ||
                            !selectedGameFormat.workspaceId
                          }>
                          Edit Game Format
                        </Button>
                      </Box>

                      {selectedGameFormat && (
                        <>
                          <GameFormatDescription gameFormat={selectedGameFormat} />
                          <OverrideGameFormat
                            gameFormat={selectedGameFormat}
                            baseGameFormat={gameFormats.find((gf) => gf.id === selectedGameFormat?.id)}
                            onChange={(gf) => handleGameFormatChange(gf)}
                          />
                        </>
                      )}
                    </>
                  )}
                </Stack>
              )}

              {currentStepLabel === 'Round Times' && (
                <Stack gap={FIELD_GAP}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h6" component="h2">
                      Round Times
                    </Typography>
                  </Box>
                  <RoundScheduleEditor
                    rows={roundScheduleRows}
                    onChange={handleRoundScheduleChange}
                    disabled={saving}
                    gameFormatRounds={selectedGameFormat?.rounds}
                    error={roundScheduleError}
                  />
                </Stack>
              )}

              {currentStepLabel === 'Green Setup' && (
                <Stack gap={FIELD_GAP}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h6" component="h2">
                      Green Setup
                    </Typography>
                    <InfoButton info={FIELD_INFO.event.greenSetup} />
                  </Box>
                  <GreenSetup ref={greenSetupRef} event={workingEvent} />
                </Stack>
              )}
              {currentStepLabel === 'Players & Teams' && (
                <Stack gap={FIELD_GAP}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h6" component="h2">
                      Players & Teams
                    </Typography>
                  </Box>

                  {!event && <PlayersImportSection saving={saving} playersImport={playersImport} />}

                  {workingEventId ? (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2
                      }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Import players from Excel
                      </Typography>
                      <ExcelPlayersImport eventId={workingEventId} />
                    </Box>
                  ) : (
                    <Alert severity="info">
                      Save the Game Format step first to unlock Excel import and roster management.
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h6" component="h2" mt={SECTION_TOP_MARGIN}>
                      Sides
                    </Typography>
                    <InfoButton info={FIELD_INFO.event.useGroups} />
                  </Box>
                  <FieldWithInfo infoKey="useGroups">
                    <Checkbox
                      name={'useGroups'}
                      label="Use Sides"
                      control={control}
                      disabled={saving}
                    />
                  </FieldWithInfo>
                  {watchUseGroups && (
                    <Box>
                      <Box
                        display="flex"
                        gap={1}
                        alignItems="center"
                        flexWrap="wrap"
                        mt={SECTION_TOP_MARGIN}>
                        <TextField
                          label="Side name"
                          size="small"
                          value={groupNameInput}
                          onChange={(event) => setGroupNameInput(event.target.value)}
                          disabled={saving}
                          sx={{ minWidth: 240 }}
                        />
                        <TextField
                          label="Color"
                          size="small"
                          type="color"
                          value={groupColorInput}
                          onChange={(event) => setGroupColorInput(event.target.value)}
                          disabled={saving}
                          sx={{ width: 90 }}
                          InputLabelProps={{ shrink: true }}
                        />
                        <Button variant="outlined" onClick={handleAddGroup} disabled={saving}>
                          Add Side
                        </Button>
                      </Box>
                      {groupError && (
                        <Typography variant="caption" color="error" display="block" mt={1}>
                          {groupError}
                        </Typography>
                      )}

                      <List
                        dense
                        sx={{
                          mt: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1
                        }}>
                        {groupEntries.length === 0 ? (
                          <ListItem>
                            <Typography variant="caption" color="textSecondary">
                              No sides added yet.
                            </Typography>
                          </ListItem>
                        ) : (
                          groupEntries.map((group) => (
                            <ListItem key={group.id}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <IconButton
                                  edge="start"
                                  aria-label="delete"
                                  color="error"
                                  onClick={() => handleRemoveGroup(group.id)}
                                  disabled={saving}
                                  size="small">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 'bold',
                                    color: group.color || 'text.primary'
                                  }}>
                                  {group.name}
                                </Typography>
                                <MuiLink
                                  component="button"
                                  type="button"
                                  underline="hover"
                                  onClick={() => groupColorInputRefs.current[group.id]?.click()}
                                  sx={{ fontSize: '0.8rem' }}>
                                  Change color
                                </MuiLink>
                                <input
                                  ref={(element) => {
                                    groupColorInputRefs.current[group.id] = element;
                                  }}
                                  type="color"
                                  value={group.color || '#1976d2'}
                                  onChange={(event) =>
                                    handleGroupColorChange(group.id, event.target.value)
                                  }
                                  disabled={saving}
                                  style={{
                                    position: 'absolute',
                                    width: 0,
                                    height: 0,
                                    opacity: 0,
                                    border: 0
                                  }}
                                  tabIndex={-1}
                                  aria-hidden="true"
                                />
                              </Box>
                            </ListItem>
                          ))
                        )}
                      </List>
                      <Typography variant="caption" color="textSecondary">
                        These sides will be available in the team dialog. Color is optional.
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h6" component="h2" mt={SECTION_TOP_MARGIN}>
                      Teams & Players
                    </Typography>
                  </Box>
                  {workingEventId && liveEvent?.id === workingEventId ? (
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <ViewPlayersDialog />
                      <ViewTeamsDialog />
                    </Box>
                  ) : (
                    <Alert severity="info">
                      Save the Game Format step first to manage players and teams.
                    </Alert>
                  )}
                </Stack>
              )}

              {currentStepLabel === 'Bracket Settings' && (
                <Stack gap={FIELD_GAP}>
                  <Typography variant="h6" component="h2">
                    Bracket Settings
                  </Typography>
                  <FieldWithInfo infoKey="hasBracketOnly">
                    <Checkbox
                      name={'hasBracketOnly'}
                      label="Bracket Only"
                      control={control}
                      error={errors.hasBracketOnly}
                      disabled={saving}
                    />
                  </FieldWithInfo>
                  {watchHasBracketOnly && (
                    <FieldWithInfo infoKey="seedingTemplate">
                      <Select
                        label="Seeding Template"
                        name="seedingTemplate"
                        control={control}
                        disabled={saving}
                        defaultValue={event?.seedingTemplate || ''}
                        sx={{ maxWidth: 320 }}>
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="pba-pairs">PBA Pairs Seeding</MenuItem>
                      </Select>
                    </FieldWithInfo>
                  )}

                  {!watchHasBracketOnly && (
                    <Stack gap={FIELD_GAP}>
                      <Typography variant="h6" component="h2" mt={SECTION_TOP_MARGIN}>
                        Section Settings
                      </Typography>
                      <FieldWithInfo infoKey="roundPublishMethod">
                        <Select
                          label="Round Publish Method"
                          name="roundPublishMethod"
                          control={control}
                          error={errors.roundPublishMethod}
                          disabled={saving}
                          defaultValue={event?.roundPublishMethod}>
                          <MenuItem value={'auto-rounds'}>
                            <Box>
                              <Typography variant="body1">Automatic Rounds</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Logs Round auto continues to next round.
                              </Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value={'publish-rounds'}>
                            <Box>
                              <Typography variant="body1">Publish Rounds</Typography>
                              <Typography variant="caption" color="textSecondary">
                                Log wait for Publish of next round.
                              </Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FieldWithInfo>
                      <FieldWithInfo infoKey="hasSections">
                        <Checkbox
                          name={'hasSections'}
                          label="Sectional Play"
                          control={control}
                          error={errors.hasSections}
                          disabled={saving}
                        />
                      </FieldWithInfo>
                    </Stack>
                  )}
                </Stack>
              )}

              {currentStepLabel === 'Scoring Settings' && (
                <Stack gap={FIELD_GAP}>
                  <Typography variant="h6" component="h2">
                    Scoring Settings
                  </Typography>

                  <FieldWithInfo infoKey="scoringMethod">
                    <Select
                      label="Scoring Method"
                      name="scoringMethod"
                      control={control}
                      error={errors.scoringMethod}
                      disabled={saving}
                      defaultValue={event?.scoringMethod}>
                      <MenuItem value={'endbyend'}>
                        <Box>
                          <Typography variant="body1">End by End</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Live scoring end by end.
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value={'inputfinalscore'}>
                        <Box>
                          <Typography variant="body1">Input Final Score</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Only input final score, no end to end live scoring.
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value={'inputtotals'}>
                        <Box>
                          <Typography variant="body1">Manual Input (Totals Only)</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Enter total shots and total game points only.
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FieldWithInfo>
                </Stack>
              )}

              {currentStepLabel === 'Log Settings' && (
                <Stack gap={FIELD_GAP}>
                  <Typography variant="h6" component="h2">
                    Log Settings
                  </Typography>
                  <FieldWithInfo infoKey="publishLogs">
                    <Checkbox
                      name={'publishLogs'}
                      label="Publish the logs"
                      control={control}
                      error={errors.publishLogs}
                      disabled={saving}
                    />
                  </FieldWithInfo>
                  <FieldWithInfo infoKey="publishScorecard">
                    <Checkbox
                      name={'publishScorecard'}
                      label="Publish the detailed score card"
                      control={control}
                      error={errors.publishScorecard}
                      disabled={saving}
                    />
                  </FieldWithInfo>
                </Stack>
              )}

              {currentStepLabel === 'Review' && (
                <Stack gap={1.5}>
                  <Typography variant="h6">Review</Typography>
                  <Typography variant="body2">Name: {watch('name') || '-'}</Typography>
                  <Typography variant="body2">
                    Dates:{' '}
                    {watch('startDate') ? moment(watch('startDate')).format('DD/MM/YYYY') : '-'} -{' '}
                    {watch('endDate') ? moment(watch('endDate')).format('DD/MM/YYYY') : '-'}
                  </Typography>
                  <Typography variant="body2">
                    Venue:{' '}
                    {watchVenueNotApplicable
                      ? 'Not Applicable'
                      : watchUseMultipleVenues
                        ? `Multiple (${(watch('locations') || []).length} selected)`
                        : venues.find((v) => v.id === watch('venue'))?.name || '-'}
                  </Typography>
                  <Typography variant="body2">
                    Game Format:{' '}
                    {gameFormats.find((gf) => gf.id === watch('gameFormat'))?.name || '-'}
                  </Typography>
                  <Typography variant="body2">
                    Round Times: {roundScheduleSummary}
                  </Typography>
                  <Typography variant="body2">
                    Sides: {watchUseGroups ? `${groupEntries.length} sides configured` : 'Disabled'}
                  </Typography>
                  <Typography variant="body2">
                    Bracket Only: {watchHasBracketOnly ? 'Yes' : 'No'}
                  </Typography>
                  <Typography variant="body2">
                    Scoring: {SCORING_METHOD_LABELS[watch('scoringMethod')] || '-'}
                  </Typography>
                  <Typography variant="body2">
                    Logs: {watch('publishLogs') ? 'Published' : 'Hidden'}
                  </Typography>
                  <Typography variant="body2">
                    Public Feeds: {watch('publishEvent') ? 'Published' : 'Hidden'}
                  </Typography>
                  <FieldWithInfo infoKey="publishEvent">
                    <Checkbox
                      name={'publishEvent'}
                      label="Publish this event's games to the public feeds"
                      control={control}
                      error={errors.publishEvent}
                      disabled={saving}
                    />
                  </FieldWithInfo>
                </Stack>
              )}
            </Box>
          </Box>
        </DialogContent>
        {!gameFormatWizardMode ? (
          <DialogActions>
            {saving ? (
              <CircularProgress />
            ) : (
              <>
                <PrimaryButton variant={'outlined'} color={'error'} onClick={() => handleClose()}>
                  Cancel
                </PrimaryButton>
                <PrimaryButton
                  variant={'outlined'}
                  onClick={handleSaveDraft}
                  disabled={!canSaveDraft}>
                  Save Draft
                </PrimaryButton>
                <PrimaryButton
                  variant={'outlined'}
                  onClick={handleSaveAndClose}
                  disabled={!canSaveDraft}>
                  Save & Close
                </PrimaryButton>
                <PrimaryButton
                  variant={'outlined'}
                  onClick={handlePreviousStep}
                  disabled={disableSave || activeStep === 0}>
                  Back
                </PrimaryButton>
                {activeStep < wizardSteps.length - 1 ? (
                  <PrimaryButton
                    variant={'contained'}
                    onClick={handleNextStep}
                    disabled={disableSave}>
                    Next
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    variant={'contained'}
                    onClick={() => handleAddEdit()}
                    disabled={disableSave}>
                    {isEditingExistingEvent ? 'Update Event' : 'Create Event'}
                  </PrimaryButton>
                )}
              </>
            )}
          </DialogActions>
        ) : null}
      </Dialog>
    </FormProvider>
  );
};

export default AddEditEventDialog;
